import React from 'react'
import styled from '@emotion/styled'
import { object, shape, string } from 'prop-types'
import dayjs from 'dayjs'
import { makeImgResizerUrl } from '@jelly/utils'

import { SponsoredLink } from '../SponsoredLink'
import { Thumbnail } from '../Thumbnail'
import { AuthorByline } from '../AuthorByline'

const AuthorBylineWrapper = styled.div`
	color: ${({ theme }) => theme.colors.gray[500]};
	display: flex;
	flex-direction: row;
	margin: 1rem 0;
`
const AuthorBylineLeft = styled.div`
	display: flex;
	height: 50px;
	margin-right: 1rem;
	width: 50px;
`
const AuthorBylineInfo = styled.div`
	align-items: flex-start;
	display: flex;
	flex-direction: column;
`
const BylineDetails = styled.div`
	border-left: 2px solid ${({ theme }) => theme.colors.gray[200]};
	font-size: 14px;
	margin-top: 0.5rem;
	padding-left: 1rem;
`
const AuthorName = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	color: ${({ theme }) => theme.colors.gray[700]};
	display: inline-block;
	font-size: 20px;
`
// const AuthorInfo = styled.div`
// 	${({ theme }) => theme.fonts.primarySemiBold};
// 	display: inline-block;
// 	font-size: 1rem;
// `
// const AuthorJobTitle = styled.span`
// 	color: ${({ theme }) => theme.colors.tertiary};
// `
// const AuthorCompany = styled.span`
// 	color: ${({ theme }) => theme.colors.gray[500]};
// `
const SponsorByline = styled.div`
	color: ${({ theme }) => theme.colors.gray[400]};
	font-size: 12px;
`
const ThumbnailWrapper = styled.div`
	width: 100%;
`
const HeaderSponsorLogo = styled.img`
	display: block;
	height: 50px;
	margin: 5px 0;
	width: auto;

	${({ theme }) => theme.MQ.M} {
		display: inline;
		margin: 8px 0;
		max-height: 50px;
		vertical-align: middle;
	}
`
const isBylineType = (type) => ['Contributing Writer', 'Old BDC Member', 'New BDC Member', 'Staff']
// If the type does not contain the word "staff", show the company info
// const shouldShowCompany = (company, type) => !/staff/i.test(type) && company

const SponsorBylineWrapper = ({ sponsorLink, sponsorName, thumbnail }) => (
	<>
		<SponsorByline>Content sponsored in partnership with {sponsorName} </SponsorByline>
		<SponsoredLink sponsorLink={sponsorLink} sponsorName={sponsorName}>
			<HeaderSponsorLogo
				alt={sponsorName}
				className="lazyload"
				data-src={makeImgResizerUrl({
					src: thumbnail,
					method: 'h',
					h: '50-png',
				})}
				advertorial
			/>
		</SponsoredLink>
	</>
)

const DefaultAuthorByline = ({ byLine, bylineDate, author }) => {
	const { name, thumbnail, type } = author

	const ThumbnailComponent = (
		// If byLine is populated, use the default thumbnail image, otherwise, use the author thumnbnail
		<Thumbnail alt={name} src={!!byLine ? null : thumbnail} size="small" variant="avatar" />
	)

	return (
		<AuthorBylineWrapper>
			<AuthorBylineLeft>
				<ThumbnailWrapper>{ThumbnailComponent}</ThumbnailWrapper>
			</AuthorBylineLeft>
			<AuthorBylineInfo>
				{/* If byline is empty, use the author info. Otherwise, use the byline */}
				{!byLine ? (
					<>
						<AuthorName>{name}</AuthorName>
						{/* If a type is passed in that is not in the list of byline types, return null */}
						{type && isBylineType(type) ? (
							<GetAuthorBylineByType author={author} bylineDate={bylineDate} type={type} />
						) : null}
					</>
				) : (
					<>
						<AuthorName>{byLine}</AuthorName>
						<BylineDetails>{bylineDate}</BylineDetails>
					</>
				)}
			</AuthorBylineInfo>
		</AuthorBylineWrapper>
	)
}

const GetAuthorBylineByType = ({ author, bylineDate, type }) => (
	<>
		{/* {author.user?.profile && (
			<AuthorInfo>
				{author.user.profile?.jobTitle && <AuthorJobTitle>{author.user.profile.jobTitle}</AuthorJobTitle>}
				{shouldShowCompany(author.user.profile?.company, type) && (
					<AuthorCompany>{` at ${author.user.profile.company}`}</AuthorCompany>
				)}
			</AuthorInfo>
		)} */}
		<BylineDetails>
			<AuthorByline type={type} />
			<div>{bylineDate}</div>
		</BylineDetails>
	</>
)

AuthorBio.propTypes = {
	author: shape({
		name: string,
	}),
	byLine: string,
	lastModified: string.isRequired,
	published: string.isRequired,
	sponsorship: object,
}

/**
 * This is the Author Bio for article and review pages
 * Used at the top of the content to display the author or sponsor info
 */
export function AuthorBio({
	author,
	byLine = '',
	lastModified,
	published,
	sponsorship = { sponsorType: null, thumbnail: null, name: null, social: { website: null } },
}) {
	const lastModifiedDate = dayjs(lastModified)
	const isUpdated = lastModifiedDate.isAfter(published)

	const bylineDate = `${isUpdated ? 'Updated ' : ''}${lastModifiedDate.format('MMM DD, YYYY')}`

	if (sponsorship === null) {
		return <DefaultAuthorByline author={author} byLine={byLine} bylineDate={bylineDate} />
	}

	const {
		sponsorType,
		name: sponsorName,
		social: { website },
		thumbnail,
	} = sponsorship

	switch (sponsorType) {
		case 'single-sponsor':
		case 'advertorial':
			return (
				<AuthorBylineWrapper>
					<AuthorBylineInfo>
						<SponsorBylineWrapper sponsorLink={website} sponsorName={sponsorName} thumbnail={thumbnail} />
						<BylineDetails>{bylineDate}</BylineDetails>
					</AuthorBylineInfo>
				</AuthorBylineWrapper>
			)
		case 'hub-sponsor':
			return (
				<>
					<DefaultAuthorByline author={author} byLine={byLine} bylineDate={bylineDate} />
					<SponsorBylineWrapper sponsorLink={website} sponsorName={sponsorName} thumbnail={thumbnail} />
				</>
			)
		default:
			return <DefaultAuthorByline author={author} byLine={byLine} bylineDate={bylineDate} />
	}
}
